// FearGreedWidget/FearGreedWidget.jsx
// Extends PainterBase — draggable card, but no cardDataListener subscription:
// FearGreedEngine polls on an interval and notifies via ComponentNotifier.
import React from 'react';
import { createPortal } from 'react-dom';
import CloseIcon from '@mui/icons-material/Close';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';

// components / controls
import JxTitleBar from '../../../JxControls/JxTitleBar/JxTitleBar';

// core
import PainterBase from '../Core/PainterBase';

// local
import fearGreedEngine from './FearGreedEngine';
import ComponentNotifier from '../../../services/wss/ComponentNotifier';
import FearGreedMeterBar from './FearGreedMeterBar';
import FearGreedHistoryChart from './FearGreedHistoryChart';
import { getFaceIcon } from './FearGreedFaceIcons';
import FearGreedSegmentCalculator from './FearGreedSegmentCalculator';
import fearGreedConst from './fearGreedConst';
import { emsToDisplay, fromNow } from '../../../services/utils/DateFormat';
import './FearGreedWidget.css';

class FearGreedWidget extends PainterBase {
    constructor(props) {
        super(props);
        this.name = `FearGreedWidget_${props.meta.target}`; // required by JxForm — unique per instance
        this.schema = null;

        this.state = { ...this.state, data: null, history: [], statsOpen: true, chartOpen: false };
    }

    /**
     * Toggles the collapsible Yesterday/Last week/Last month stats row.
     */
    _toggleStats = () => {
        this.setState((s) => ({ statsOpen: !s.statsOpen }));
    };

    /**
     * Toggles the 31-day history chart.
     */
    _toggleChart = () => {
        this.setState((s) => ({ chartOpen: !s.chartOpen }));
    };

    componentDidMount() {
        super.componentDidMount();
        this._unsubscribe = ComponentNotifier.subscribe('FearGreedEngine', (e) => this.onPaint(e.data.state));

        const existing = fearGreedEngine.getState();
        if (existing) this.onPaint(existing);
    }

    componentWillUnmount() {
        if (this._unsubscribe) this._unsubscribe();
        super.componentWillUnmount();
    }

    componentDidUpdate(prevProps) {
        super.componentDidUpdate(prevProps);
    }

    getDefaultGeo() {
        return { top: 120, left: 120, width: 300, height: 260 };
    }

    // PainterBase contract — called on every ComponentNotifier 'dataChanged' event.
    onPaint(state) {
        this.setState({ data: state, history: fearGreedEngine.getHistory() });
    }

    /**
     * @returns {object[]} JxTitleBar actions — pin toggle + close.
     */
    getTitleBarActions() {
        return [
            {
                icon: this.state.isPinned ? <PushPinIcon sx={{ fontSize: 14 }} /> : <PushPinOutlinedIcon sx={{ fontSize: 14 }} />,
                title: this.state.isPinned ? 'Unpin' : 'Pin on top',
                onClick: this.togglePin,
            },
            {
                icon: <CloseIcon sx={{ fontSize: 16 }} />,
                title: 'Close',
                onClick: () => this.handleClose(-1),
            },
        ];
    }

    render() {
        if (!this.props.open) return null;

        const { data } = this.state;
        const panel = (
            <div {...this.panelProps} style={this.panelStyle} className={`fear-greed-widget ${this.panelActiveClass} ${this.panelPinnedClass}`}>
                <JxTitleBar
                    id={`${this.name}_titlebar`}
                    title="fear & greed index"
                    variant="toolwindow"
                    isDraggable={true}
                    dragHandleProps={this.dragHandleProps}
                    actions={this.getTitleBarActions()}
                />
                <div className="fgw-body">{data ? this._renderData(data) : <div className="fgw-loading">waiting for data…</div>}</div>
            </div>
        );

        return createPortal(panel, document.body);
    }

    _renderData(data) {
        const segment = FearGreedSegmentCalculator.getSegment(data.value);

        return (
            <>
                <div className="fgw-top-row">
                    <FearGreedMeterBar value={data.value} />
                    <span className="fgw-value">{data.value}</span>
                </div>

                <div className="fgw-zone-labels">
                    {fearGreedConst.SEGMENTS.map((s) => (
                        <span key={s.icon}>{s.shortLabel}</span>
                    ))}
                </div>

                <div className="fgw-state-row">
                    <button type="button" className="fgw-state-left" onClick={this._toggleChart}>
                        {getFaceIcon(segment.icon, segment.color)}
                        <span className="fgw-state-label" style={{ color: segment.color }}>{segment.label}</span>
                        <i className={`ti ${this.state.chartOpen ? 'ti-chevron-up' : 'ti-chevron-down'}`} aria-hidden="true" />
                    </button>
                    <button type="button" className="fgw-updated" onClick={this._toggleStats} title={emsToDisplay(data.lastUpdated)}>
                        <i className="ti ti-clock" aria-hidden="true" />
                        <span>{fromNow(data.lastUpdated)}</span>
                        <i className={`ti ${this.state.statsOpen ? 'ti-chevron-up' : 'ti-chevron-down'}`} aria-hidden="true" />
                    </button>
                </div>

                {this.state.chartOpen && <FearGreedHistoryChart history={this.state.history} />}

                {this.state.statsOpen && (
                    <div className="fgw-stats-row">
                        <div className="fgw-stat"><div className="fgw-stat-label">Yesterday</div><div className="fgw-stat-value">{data.yesterday}</div></div>
                        <div className="fgw-stat"><div className="fgw-stat-label">Last week</div><div className="fgw-stat-value">{data.lastWeek}</div></div>
                        <div className="fgw-stat"><div className="fgw-stat-label">Last month</div><div className="fgw-stat-value">{data.lastMonth}</div></div>
                    </div>
                )}
            </>
        );
    }
}

export default FearGreedWidget;
